import React, { useEffect, useMemo, useState } from 'react';
import { useLang } from '../../contexts/LangContext';
import { useAuth } from '../../contexts/AuthContext';
import { apartmentsApi, type ApartmentMember, type Building, type ResidentActivityHistoryItem } from '../../api/apartmentsApi';
import { applyDefaultBuildingSelection, needsBuildingSelection, shouldShowBuildingSelector, getBuildingSelectProps, selectBuilding } from '../../utils/buildingSelection';
import ResidentFormModal from '../../components/modals/ResidentFormModal';
import { MobileAppLinkStatus } from '../../components/residents/MobileAppLinkStatus';
import { getApiErrorMessage } from '../../utils/apiErrors';
import { formatDateToDisplay } from '../../utils/apartmentDate';
import {
  getRelationshipLabelByCode,
  getResidentRoleLabel,
} from '../../utils/residentLookup';
import { useFunctionPermissions } from '../../hooks/useFunctionPermissions';
import ImportFileLink from '../../components/common/ImportFileLink';
import { BulkListPagination } from '../../components/bulk-fee/BulkListPagination';
import { HISTORY_PAGE_SIZE } from '../../api/paginatedHistory';

type ResidentListTab = 'LIST' | 'HISTORY';

const OWNER_ROLES = new Set(['CHU_HO', 'OWNER', 'CORPORATE_REPRESENTATIVE']);

function residentMatchesSearch(resident: ApartmentMember, term: string): boolean {
  const q = term.trim().toLowerCase();
  if (!q) return true;
  const phone = resident.phone?.replace(/\s/g, '') ?? '';
  const qPhone = term.trim().replace(/\s/g, '');
  return (
    resident.fullName.toLowerCase().includes(q)
    || (resident.email?.toLowerCase().includes(q) ?? false)
    || (phone && qPhone && phone.includes(qPhone))
    || (resident.citizenId?.toLowerCase().includes(q) ?? false)
    || (resident.apartment?.code?.toLowerCase().includes(q) ?? false)
    || (resident.apartment?.name?.toLowerCase().includes(q) ?? false)
  );
}

const ResidentList: React.FC = () => {
  const { t } = useLang();
  const { user, isSuperAdmin, isOperator } = useAuth();
  const { can } = useFunctionPermissions();
  const canImportResidents = can('RESIDENT_IMPORT', 'import');
  const canBulkDelete = isSuperAdmin || isOperator;
  const [allResidents, setAllResidents] = useState<ApartmentMember[]>([]);
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBuildingId, setSelectedBuildingId] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(25);
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingResident, setEditingResident] = useState<ApartmentMember | null>(null);
  const [exporting, setExporting] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importMode, setImportMode] = useState<'skip' | 'replace'>('skip');
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [importSuccess, setImportSuccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<ResidentListTab>('LIST');
  const [activityHistory, setActivityHistory] = useState<ResidentActivityHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyTotalPages, setHistoryTotalPages] = useState(1);
  const [expandedHistoryId, setExpandedHistoryId] = useState<string | null>(null);
  const [selectedResidentIds, setSelectedResidentIds] = useState<string[]>([]);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);

  useEffect(() => {
    loadBuildings();
  }, []);

  useEffect(() => {
    if (selectedBuildingId) {
      loadResidents();
    } else {
      setAllResidents([]);
      setLoading(false);
    }
  }, [selectedBuildingId]);

  useEffect(() => {
    setSelectedResidentIds([]);
    setHistoryPage(1);
  }, [selectedBuildingId, searchTerm, activeTab]);

  useEffect(() => {
    if (selectedBuildingId && activeTab === 'HISTORY') {
      loadActivityHistory();
    } else if (!selectedBuildingId) {
      setActivityHistory([]);
    }
  }, [selectedBuildingId, activeTab, historyPage]);

  const filteredResidents = useMemo(
    () => allResidents.filter((resident) => residentMatchesSearch(resident, searchTerm)),
    [allResidents, searchTerm],
  );

  const totalRecords = filteredResidents.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / recordsPerPage) || 1);
  const startIndex = (currentPage - 1) * recordsPerPage;
  const endIndex = startIndex + recordsPerPage;
  const currentResidents = useMemo(
    () => filteredResidents.slice(startIndex, endIndex),
    [filteredResidents, startIndex, endIndex],
  );
  const listEndIndex = Math.min(endIndex, totalRecords);

  const ownerCount = useMemo(
    () => allResidents.filter((resident) => OWNER_ROLES.has(resident.role)).length,
    [allResidents],
  );
  const appLinkedCount = useMemo(
    () => allResidents.filter((resident) => Boolean(resident.userId && resident.user)).length,
    [allResidents],
  );

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const loadActivityHistory = async () => {
    if (!selectedBuildingId) return;
    try {
      setHistoryLoading(true);
      const result = await apartmentsApi.listResidentActivityHistory(selectedBuildingId, {
        page: historyPage,
        limit: HISTORY_PAGE_SIZE,
      });
      setActivityHistory(result.data ?? []);
      setHistoryTotal(result.meta?.total ?? 0);
      setHistoryTotalPages(result.meta?.totalPages ?? 1);
    } catch (err) {
      console.error('Error loading resident activity history:', err);
      setActivityHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const loadBuildings = async () => {
    try {
      const data = await apartmentsApi.getBuildings();
      setBuildings(data);
      setSelectedBuildingId((prev) => applyDefaultBuildingSelection(data, user, prev));
    } catch (err) {
      setError(t('failedToLoad'));
    }
  };

  const loadResidents = async () => {
    if (!selectedBuildingId) return;

    try {
      setLoading(true);
      setError(null);
      const data = await apartmentsApi.getResidentsByBuilding(selectedBuildingId);
      setAllResidents(data);
    } catch (err) {
      setError(t('errorLoadingResidents'));
      setAllResidents([]);
    } finally {
      setLoading(false);
    }
  };

  const handleBuildingChange = (buildingId: string) => {
    selectBuilding(user?.id, buildingId, setSelectedBuildingId);
    setCurrentPage(1);
    setSearchTerm('');
  };

  const handleRecordsPerPageChange = (records: number) => {
    setRecordsPerPage(records);
    setCurrentPage(1);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('confirmDeleteResident'))) return;
    try {
      await apartmentsApi.deleteApartmentMember(id);
      setSelectedResidentIds((prev) => prev.filter((residentId) => residentId !== id));
      await loadResidents();
      if (activeTab === 'HISTORY') {
        loadActivityHistory();
      }
    } catch (err) {
      setError(getApiErrorMessage(err, t('errorDeletingResident')));
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedBuildingId || selectedResidentIds.length === 0) {
      return;
    }

    const confirmMessage = t('confirmBulkDeleteResidents').replace(
      '{count}',
      selectedResidentIds.length.toString(),
    );
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setBulkDeleteLoading(true);
      setError(null);
      const result = await apartmentsApi.bulkDeleteResidents(selectedBuildingId, selectedResidentIds);
      const deletedIds = new Set(result.deletedMemberIds);
      setSelectedResidentIds((prev) => prev.filter((id) => !deletedIds.has(id)));
      await loadResidents();

      const summary = t('bulkDeleteResidentsResultSummary').replace(
        '{deleted}',
        result.deletedCount.toString(),
      );
      if (result.deletedCount > 0) {
        setImportSuccess(summary);
      }

      if (activeTab === 'HISTORY') {
        loadActivityHistory();
      }
    } catch (err) {
      setError(getApiErrorMessage(err, t('errorDeletingResident')));
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  const toggleResidentSelection = (id: string) => {
    setSelectedResidentIds((prev) =>
      prev.includes(id) ? prev.filter((residentId) => residentId !== id) : [...prev, id],
    );
  };

  const toggleSelectAllOnPage = () => {
    const pageIds = currentResidents.map((resident) => resident.id);
    const allSelected = pageIds.length > 0 && pageIds.every((id) => selectedResidentIds.includes(id));
    if (allSelected) {
      setSelectedResidentIds((prev) => prev.filter((id) => !pageIds.includes(id)));
      return;
    }
    setSelectedResidentIds((prev) => Array.from(new Set([...prev, ...pageIds])));
  };

  const getActivityKindLabel = (item: ResidentActivityHistoryItem) => {
    switch (item.kind) {
      case 'CREATE':
        return t('addResident');
      case 'UPDATE':
        return t('activityHistoryUpdate');
      case 'DELETE':
        return t('activityHistoryDelete');
      case 'BULK_DELETE':
        return t('activityHistoryBulkDelete');
      case 'IMPORT':
        return t('activityHistoryImport');
      default:
        return item.kind;
    }
  };

  const renderActivityDescription = (item: ResidentActivityHistoryItem) => {
    if (item.memberName && item.apartmentCode) {
      return `${item.memberName} (${item.apartmentCode}) — ${item.summary || ''}`;
    }
    return item.summary || '—';
  };

  const renderActivityChanges = (metadata?: Record<string, unknown> | null) => {
    const changes = metadata?.changes;
    if (!Array.isArray(changes) || changes.length === 0) {
      return null;
    }

    return (
      <div className="space-y-1">
        {changes.map((change, index) => {
          if (!change || typeof change !== 'object') {
            return null;
          }
          const label = 'label' in change ? String(change.label) : '';
          const oldValue = 'oldValue' in change ? String(change.oldValue) : '';
          const newValue = 'newValue' in change ? String(change.newValue) : '';
          return (
            <div key={`${label}-${index}`}>
              {label}: {oldValue} → {newValue}
            </div>
          );
        })}
      </div>
    );
  };

  const renderBulkDeleteDetails = (metadata?: Record<string, unknown> | null) => {
    const deletedNames = metadata && Array.isArray(metadata.deletedNames)
      ? metadata.deletedNames as Array<{ name?: string; apartmentCode?: string }>
      : [];

    if (deletedNames.length === 0) {
      return null;
    }

    return (
      <div>
        <strong>{t('activityHistoryDeletedResidents')}:</strong>{' '}
        {deletedNames
          .slice(0, 50)
          .map((item) => `${item.name} (${item.apartmentCode})`)
          .join(', ')}
        {deletedNames.length > 50 ? ` (+${deletedNames.length - 50})` : ''}
      </div>
    );
  };

  const handleExport = async () => {
    if (!selectedBuildingId) {
      setError(t('pleaseSelectBuilding'));
      return;
    }
    try {
      setExporting(true);
      setError(null);
      await apartmentsApi.exportResidents(selectedBuildingId);
    } catch (err) {
      setError(getApiErrorMessage(err, t('failedToLoad')));
    } finally {
      setExporting(false);
    }
  };

  const handleImport = async () => {
    if (!selectedBuildingId || !importFile) {
      setImportError(t('clickToSelectFile'));
      return;
    }

    try {
      setImportLoading(true);
      setImportError(null);
      const result = await apartmentsApi.importResidents(selectedBuildingId, importFile, importMode);
      await loadActivityHistory();
      if (!result.success) {
        setImportError(result.errors?.join('\n') || result.message);
        return;
      }
      setImportSuccess(result.message);
      await loadResidents();
    } catch (err) {
      setImportError(getApiErrorMessage(err, t('importFailed')));
    } finally {
      setImportLoading(false);
    }
  };

  const openImportModal = () => {
    setImportFile(null);
    setImportError(null);
    setImportSuccess(null);
    setShowImportModal(true);
  };

  const formatCell = (value?: string | null) => {
    if (!value?.trim()) return '-';
    return value;
  };

  const selectedBuilding = buildings.find((b) => b.id === selectedBuildingId);

  if (loading && !selectedBuildingId) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-600">{t('loading')}</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">{t('residentList')}</h1>
        <div className="flex space-x-3">
          <button
            onClick={() => apartmentsApi.downloadSampleResidents()}
            className="inline-flex items-center px-4 py-2 bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            {t('downloadSampleFile')}
          </button>
          <button
            onClick={handleExport}
            disabled={exporting || !selectedBuildingId}
            className="inline-flex items-center px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors disabled:opacity-50"
          >
            {exporting ? t('exporting') : t('exportResidents')}
          </button>
          {canImportResidents && (
            <button
              onClick={openImportModal}
              disabled={!selectedBuildingId}
              className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 disabled:opacity-50"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
              </svg>
              <span>{t('importResidents')}</span>
            </button>
          )}
          <button
            onClick={() => { setEditingResident(null); setShowFormModal(true); }}
            disabled={!selectedBuildingId}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
          >
            {t('addResident')}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {shouldShowBuildingSelector(buildings) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('selectBuilding')}
              </label>
              <select
                value={selectedBuildingId}
                onChange={(e) => handleBuildingChange(e.target.value)}
                disabled={!needsBuildingSelection(buildings)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">{t('selectBuildingPlaceholder')}</option>
                {buildings.map((building) => (
                  <option key={building.id} value={building.id}>
                    {building.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          {selectedBuilding && (
            <div className="flex items-end">
              <div className="text-sm text-gray-600">
                <div>
                  <span className="font-medium">{t('operator')}:</span>{' '}
                  {selectedBuilding.operator?.name || '-'}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {selectedBuildingId && (
        <div className="flex border-b border-gray-200 mb-6">
          <button
            type="button"
            onClick={() => setActiveTab('LIST')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'LIST'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t('residentListTab')}
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('HISTORY')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'HISTORY'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t('residentHistoryTab')}
          </button>
        </div>
      )}

      {activeTab === 'HISTORY' ? (
        !selectedBuildingId ? (
          <div className="text-center py-12">
            <div className="text-gray-500 text-lg mb-4">{t('pleaseSelectBuildingToViewResidents')}</div>
          </div>
        ) : historyLoading ? (
          <div className="flex justify-center items-center h-64">
            <div className="text-gray-600">{t('loading')}</div>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">{t('activityHistoryKind')}</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">{t('importHistoryDate')}</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">{t('importHistoryStaff')}</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">{t('activityHistoryDescription')}</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">{t('importHistoryFileName')}</th>
                  <th className="px-4 py-3 text-left font-semibold text-gray-600">{t('importHistoryDetails')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {activityHistory.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                      {t('importHistoryNoData')}
                    </td>
                  </tr>
                ) : (
                  activityHistory.map((item) => (
                    <React.Fragment key={item.id}>
                      <tr className="hover:bg-gray-50">
                        <td className="px-4 py-3">{getActivityKindLabel(item)}</td>
                        <td className="px-4 py-3">{new Date(item.createdAt).toLocaleString()}</td>
                        <td className="px-4 py-3">{item.creator?.name || '—'}</td>
                        <td className="px-4 py-3">{renderActivityDescription(item)}</td>
                        <td className="px-4 py-3">
                          <ImportFileLink fileName={item.fileName} fileUrl={item.fileUrl} />
                        </td>
                        <td className="px-4 py-3">
                          {(item.kind === 'BULK_DELETE' || item.kind === 'UPDATE') && (
                            <button
                              type="button"
                              onClick={() => setExpandedHistoryId(expandedHistoryId === item.id ? null : item.id)}
                              className="text-blue-600 hover:text-blue-800"
                            >
                              {expandedHistoryId === item.id ? t('close') : t('importHistoryDetails')}
                            </button>
                          )}
                        </td>
                      </tr>
                      {expandedHistoryId === item.id && item.kind === 'BULK_DELETE' && (
                        <tr>
                          <td colSpan={6} className="px-4 py-3 bg-gray-50 text-xs text-gray-700">
                            {renderBulkDeleteDetails(item.metadata)}
                          </td>
                        </tr>
                      )}
                      {expandedHistoryId === item.id && item.kind === 'UPDATE' && (
                        <tr>
                          <td colSpan={6} className="px-4 py-3 bg-gray-50 text-xs text-gray-700">
                            {renderActivityChanges(item.metadata)}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
            <BulkListPagination
              page={historyPage}
              totalPages={historyTotalPages}
              total={historyTotal}
              onPageChange={setHistoryPage}
              t={t}
            />
          </div>
        )
      ) : !selectedBuildingId ? (
        <div className="text-center py-12">
          <div className="text-gray-500 text-lg mb-4">{t('pleaseSelectBuildingToViewResidents')}</div>
        </div>
      ) : loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="text-gray-600">{t('loading')}</div>
        </div>
      ) : allResidents.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-500 text-lg mb-4">{t('noResidents')}</div>
        </div>
      ) : (
        <>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <div className="flex-1 max-w-md">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('searchResidentsPlaceholder')}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex flex-wrap items-center gap-3">
              {canBulkDelete && selectedResidentIds.length > 0 && (
                <button
                  type="button"
                  onClick={handleBulkDelete}
                  disabled={bulkDeleteLoading}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {bulkDeleteLoading
                    ? t('loading')
                    : `${t('deleteSelectedResidents')} (${selectedResidentIds.length})`}
                </button>
              )}
              <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">{t('recordsPerPage')}:</span>
              <select
                value={recordsPerPage}
                onChange={(e) => handleRecordsPerPageChange(Number(e.target.value))}
                className="border border-gray-300 rounded px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="text-2xl font-bold text-blue-600">{allResidents.length}</div>
              <div className="text-gray-600">{t('totalResidents')}</div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="text-2xl font-bold text-green-600">{ownerCount}</div>
              <div className="text-gray-600">{t('totalOwners')}</div>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="text-2xl font-bold text-purple-600">{appLinkedCount}</div>
              <div className="text-gray-600">{t('appLinkedResidents')}</div>
            </div>
          </div>

          {totalRecords > 0 && (
            <div className="flex justify-between items-center mb-4">
              <div className="text-sm text-gray-600">
                {t('showingRecords')
                  .replace('{from}', (startIndex + 1).toString())
                  .replace('{to}', listEndIndex.toString())
                  .replace('{total}', totalRecords.toString())}
              </div>
              <div className="text-sm text-gray-600">
                {t('page')} {currentPage} {t('of')} {totalPages}
              </div>
            </div>
          )}

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {canBulkDelete && (
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <input
                          type="checkbox"
                          checked={
                            currentResidents.length > 0
                            && currentResidents.every((resident) => selectedResidentIds.includes(resident.id))
                          }
                          onChange={toggleSelectAllOnPage}
                          aria-label={t('selectAllOnPage')}
                        />
                      </th>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('stt')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('residentApartmentCode')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('residentName')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('residentRole')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('email')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('phone')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('relationshipRole')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('moveInDate')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('mobileAppAccount')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('actions')}</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentResidents.length === 0 ? (
                    <tr>
                      <td colSpan={canBulkDelete ? 11 : 10} className="px-6 py-12 text-center text-gray-500">
                        {searchTerm ? t('noData') : t('noResidents')}
                      </td>
                    </tr>
                  ) : (
                    currentResidents.map((resident, index) => (
                      <tr key={resident.id} className="hover:bg-gray-50 transition-colors">
                        {canBulkDelete && (
                          <td className="px-4 py-4 whitespace-nowrap">
                            <input
                              type="checkbox"
                              checked={selectedResidentIds.includes(resident.id)}
                              onChange={() => toggleResidentSelection(resident.id)}
                              aria-label={resident.fullName}
                            />
                          </td>
                        )}
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {startIndex + index + 1}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {resident.apartment?.code || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {resident.fullName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {getResidentRoleLabel(resident.role, t)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 max-w-[180px] truncate" title={resident.email || undefined}>
                          {formatCell(resident.email)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatCell(resident.phone)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {getRelationshipLabelByCode(resident.relationshipCode, t) || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDateToDisplay(resident.moveInDate) || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <MobileAppLinkStatus member={resident} t={t} compact />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                          <button
                            onClick={() => { setEditingResident(resident); setShowFormModal(true); }}
                            className="text-blue-600 hover:text-blue-800 transition-colors"
                          >
                            {t('edit')}
                          </button>
                          <button
                            onClick={() => handleDelete(resident.id)}
                            className="text-red-600 hover:text-red-800 transition-colors"
                          >
                            {t('delete')}
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {totalPages > 1 && (
            <div className="flex justify-center items-center space-x-2 mt-6">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                {t('first')}
              </button>
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                {t('previous')}
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pageNum = currentPage - 2 + i;
                if (pageNum > 0 && pageNum <= totalPages) {
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`px-3 py-1 border rounded text-sm ${
                        currentPage === pageNum
                          ? 'bg-blue-500 text-white border-blue-500'
                          : 'border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                }
                return null;
              })}
              <button
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                {t('next')}
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                {t('last')}
              </button>
            </div>
          )}
        </>
      )}

      <ResidentFormModal
        isOpen={showFormModal}
        onClose={() => { setShowFormModal(false); setEditingResident(null); }}
        onSuccess={async () => {
          await loadResidents();
          if (activeTab === 'HISTORY') {
            await loadActivityHistory();
          }
        }}
        buildingId={selectedBuildingId}
        resident={editingResident}
      />

      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center p-6 border-b border-gray-200 bg-gray-50 rounded-t-lg">
              <h2 className="text-xl font-bold text-gray-800">{t('importResidents')}</h2>
              <button
                onClick={() => setShowImportModal(false)}
                className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-200 rounded-full transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              <p className="text-gray-600 mb-6 text-sm leading-relaxed">{t('importResidentsHint')}</p>

              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-700 mb-3">{t('step1DownloadSample')}</h3>
                <button
                  onClick={() => apartmentsApi.downloadSampleResidents()}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span>{t('downloadSampleFile')}</span>
                </button>
              </div>

              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-700 mb-3">{t('step2UploadFile')}</h3>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                  <input
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                    className="hidden"
                    id="resident-import-file"
                  />
                  <label htmlFor="resident-import-file" className="cursor-pointer flex flex-col items-center space-y-2">
                    <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                    </svg>
                    <span className="text-gray-600 text-sm">
                      {importFile ? importFile.name : t('clickToSelectFile')}
                    </span>
                  </label>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">{t('importMode')}</label>
                <select
                  value={importMode}
                  onChange={(e) => setImportMode(e.target.value as 'skip' | 'replace')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="skip">{t('importUsageTypesModeSkip')}</option>
                  <option value="replace">{t('importUsageTypesModeReplace')}</option>
                </select>
              </div>

              {importError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm max-h-40 overflow-y-auto whitespace-pre-wrap">
                  {importError}
                </div>
              )}
              {importSuccess && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4 text-sm">
                  {importSuccess}
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 bg-gray-50 rounded-b-lg flex justify-end space-x-3">
              <button
                onClick={() => setShowImportModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
              >
                {t('close')}
              </button>
              <button
                onClick={handleImport}
                disabled={importLoading || !importFile}
                className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {importLoading ? t('importing') : t('import')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResidentList;
